import { useState } from 'react';

import DateTimeUtils from '../../utils/datetime-utils';
import { formatDateTime } from '../../constants/app-constants';

/**
 * NMU-580: the วันที่/เวลาสร้าง column, and the sort behind it.
 *
 * One helper rather than the same twenty lines in each of the eleven module
 * tables — the ticket asks for the column to look and behave the same
 * everywhere, and a copy per page is how that stops being true.
 *
 * The sort is done by the server, not by the table. Every one of these tables is
 * paged server-side (ten rows of hundreds), so ordering the rows the browser
 * holds would sort a page rather than the list — page 2 of a "newest first"
 * table would still be whatever the unsorted query put there. `sorter: true`
 * tells antd exactly that: put the arrows in the header, report the click, and
 * do not touch the rows.
 */

/** The creation stamp on every document — see DocumentBean in the common jar. */
export const CREATED_FIELD = 'createTime';

export const SORT_ASC = 'ASC';
export const SORT_DESC = 'DESC';

/** Newest first, as the ticket asks. */
export const DEFAULT_CREATED_SORT = { sortBy: CREATED_FIELD, sortOrder: SORT_DESC };

export const CREATED_COLUMN_TITLE = 'วันที่/เวลาสร้าง';

/**
 * A record with no stamp prints a dash.
 *
 * Migrated records can arrive without one, and moment turns a null into
 * "Invalid date" — which reads like a broken column rather than an empty field.
 */
export const formatCreatedAt = (value) =>
  value ? DateTimeUtils.parseDateToString(value, formatDateTime.DATETIME_SHORT) : '-';

/**
 * @param onSort called with the new sort when the header is clicked. It receives
 *   the sort explicitly because setState only lands on the next render, so a
 *   caller that re-searched from state here would re-run the *previous* order —
 *   the same reason doSearch takes overrides (NMU-526/529).
 */
export const useCreatedSort = (onSort) => {
  const [sort, setSort] = useState(DEFAULT_CREATED_SORT);

  const onChangeTable = (_pagination, _filters, sorter) => {
    const nextOrder = sorter && sorter.order === 'ascend' ? SORT_ASC : SORT_DESC;

    if (nextOrder === sort.sortOrder) {
      return;
    }

    const next = { sortBy: CREATED_FIELD, sortOrder: nextOrder };
    setSort(next);
    onSort(next);
  };

  const createdColumn = {
    title: CREATED_COLUMN_TITLE,
    key: CREATED_FIELD,
    dataIndex: CREATED_FIELD,
    width: 160,
    // true, not a comparator: the rows are one page of a server-side sort.
    sorter: true,
    /*
      Two states, not antd's default three. The list always comes back in some
      order, so "no sort" is not a thing the server can answer — offering it
      would show a header with no arrow highlighted over rows that are in fact
      still sorted.
    */
    sortDirections: ['descend', 'ascend'],
    sortOrder: sort.sortOrder === SORT_ASC ? 'ascend' : 'descend',
    render: (value) => formatCreatedAt(value),
  };

  return { sort, createdColumn, onChangeTable };
};

/**
 * The column without the sort, for the two tables whose row order *is* data.
 *
 * Slider and Intranet Management are arrangement editors: their first column is
 * ลำดับ with move-up/move-down buttons, and saving writes the sequence the rows
 * are currently in. Re-ordering those rows by creation date would leave the
 * arrows moving a row against a sequence nobody set, and the save would then
 * write that back over the arrangement. So they show when a card was made — the
 * ticket wants the date visible — and leave the ordering to the buttons.
 */
export const createdColumnStatic = {
  title: CREATED_COLUMN_TITLE,
  key: CREATED_FIELD,
  dataIndex: CREATED_FIELD,
  width: 160,
  render: (value) => formatCreatedAt(value),
};

/**
 * The sort for a table that already holds every row.
 *
 * Programs fetches the whole list and cuts pages out of it in the browser, so
 * there is no query to re-run — but antd would still only sort the page it was
 * handed, because the slicing happens before the Table sees the rows. Hence a
 * comparator applied to the full list here, ahead of that slice.
 */
export const useClientCreatedSort = () => {
  const [sortOrder, setSortOrder] = useState(SORT_DESC);

  const onChangeTable = (_pagination, _filters, sorter) => {
    setSortOrder(sorter && sorter.order === 'ascend' ? SORT_ASC : SORT_DESC);
  };

  const createdColumn = {
    ...createdColumnStatic,
    sorter: true,
    sortDirections: ['descend', 'ascend'],
    sortOrder: sortOrder === SORT_ASC ? 'ascend' : 'descend',
  };

  /** Rows with no stamp sort last either way, rather than jumping to the top. */
  const sortRows = (rows) => {
    const stamp = (row) => {
      const value = row && row[CREATED_FIELD];
      return value ? new Date(value).getTime() : null;
    };

    return [...(rows || [])].sort((a, b) => {
      const left = stamp(a);
      const right = stamp(b);

      if (left === right) return 0;
      if (left === null) return 1;
      if (right === null) return -1;

      return sortOrder === SORT_ASC ? left - right : right - left;
    });
  };

  return { sortOrder, createdColumn, onChangeTable, sortRows };
};

/**
 * Puts the column in front of ดำเนินการ.
 *
 * The action column is `fixed: 'right'` in every table, so appending would leave
 * วันที่/เวลาสร้าง floating to the right of the pinned actions on a narrow
 * screen. Tables with no action column get it last, which is the same place.
 */
export const withCreatedColumn = (columns, createdColumn) => {
  const actionAt = columns.findIndex((c) => c && c.key === 'action');

  if (actionAt < 0) {
    return [...columns, createdColumn];
  }

  return [...columns.slice(0, actionAt), createdColumn, ...columns.slice(actionAt)];
};
